import type { AssignedInquiry } from '@ghp/domain'
import { Badge, Button, Card, Modal, TextLink } from '@ghp/ui'
import { Link } from '@tanstack/react-router'
import { ChevronRight, FileWarning, Paperclip, Trash2 } from 'lucide-react'
import { useId, useState } from 'react'
import { townOf } from '@ghp/domain'

import type { DiscardResult } from '../../../offline/discard-out-of-tour'
import { stampLabel, SYNC_PRESENTATION } from '../labels'
import type { OrphanDraft } from '../lib/orphan-drafts'
import s from './OrphanDrafts.module.css'

/**
 * « Saisies sans contrôle dans la tournée » — le chemin d'écran vers un relevé dont la
 * demande a quitté la tournée servie (#147).
 *
 * Sans cette section, une saisie non transmise dont le bureau a réaffecté la demande n'est
 * plus atteignable que par l'URL du contrôle : elle disparaît de « Mes contrôles », qui part
 * des demandes. Elle n'est pourtant ni effacée ni bloquée — d'où le parti M21 : on la montre,
 * on dit ce qu'on sait, et l'agent tranche.
 *
 * ⚠️ **Présentationnel pur**, comme l'éditeur : aucune I/O, tout arrive en props.
 *
 * **Retirer de la tablette** (#668) : la descente garde ces saisies — elle ne jette pas une
 * saisie —, y compris celles dont le contrôle a été supprimé au bureau et qui n'ont plus où
 * arriver. Le geste est offert ici, et seulement ici : c'est le seul écran qui dit qu'une saisie
 * est hors tournée. Irréversible, il passe par une confirmation qui dit ce qui sera perdu, et
 * **rien de plus que ce que la tablette sait** : hors tournée ne veut pas dire supprimé — une
 * réaffectation est le cas courant, et le serveur rangerait encore ce relevé.
 */
export interface OrphanDraftsProps {
  rows: OrphanDraft[]
  /** Absent, le geste n'est pas offert. */
  onDiscard?: (inquiryId: string) => Promise<DiscardResult>
}

/** Pourquoi rien n'a été retiré, dit dans la boîte, qui reste ouverte. */
const DISCARD_REFUSAL: Record<Exclude<DiscardResult, 'removed'>, string> = {
  sending:
    "Un envoi de ce contrôle est en cours : rien n'a été retiré. Réessayez une fois l'envoi terminé.",
  served: "La dernière tournée reçue porte de nouveau ce contrôle : rien n'a été retiré.",
}

export function OrphanDrafts({ rows, onDiscard }: OrphanDraftsProps) {
  const titleId = useId()
  const [discarding, setDiscarding] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (rows.length === 0) return null

  function close(): void {
    setDiscarding(null)
    setError(null)
  }

  async function confirm(inquiryId: string): Promise<void> {
    if (onDiscard === undefined) return
    setBusy(true)
    try {
      const result = await onDiscard(inquiryId)
      if (result === 'removed') close()
      else setError(DISCARD_REFUSAL[result])
    } catch (failure) {
      console.error('[terrain] Retrait du contrôle impossible', failure)
      setError("Le contrôle n'a pas pu être retiré de la tablette. Réessayez.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className={s.root} aria-labelledby={titleId}>
      <h2 className={s.title} id={titleId}>
        <FileWarning size={19} aria-hidden />
        Saisies sans contrôle dans la tournée
        <Badge tone="warn">{rows.length}</Badge>
      </h2>

      <p className={s.reason}>
        Ces relevés ont été faits sur cette tablette et ne sont pas encore transmis, alors que la
        dernière tournée reçue ne portait plus leur demande — c'est ce qui arrive quand le bureau la
        confie à une autre équipe. Rien n'est perdu : le relevé est intact. Transmettez-le pour
        qu'il ne reste pas sur cette seule tablette.
      </p>

      <Card className={s.list}>
        {rows.map((row) => (
          <OrphanRow
            key={row.draft.inquiryId}
            row={row}
            onDiscard={
              onDiscard === undefined
                ? undefined
                : () => {
                    setError(null)
                    setDiscarding(row.draft.inquiryId)
                  }
            }
          />
        ))}
      </Card>

      <Modal
        open={discarding !== null}
        onOpenChange={(open) => {
          if (!open) close()
        }}
        icon={<Trash2 size={18} />}
        title="Retirer ce contrôle de la tablette ?"
        // ⚠️ Ce que la tablette sait, rien de plus : la dernière tournée ne porte plus la demande.
        // Une réaffectation est le cas courant, et le serveur rangerait encore ce relevé (M21).
        subtitle="Ce contrôle n'est plus dans la tournée de votre équipe : il a été supprimé au bureau, ou confié à une autre équipe. Le retirer supprime définitivement de la tablette sa saisie, ses photos et ses envois en attente : rien ne sera transmis. Si cette saisie peut servir, transmettez-la plutôt."
        busy={busy}
        error={error}
        footer={
          <>
            {/* Le focus va sur le geste qui ne perd rien, comme au retrait d'une pièce. */}
            <Button tone="ghost" autoFocus disabled={busy} onClick={close}>
              Annuler
            </Button>
            <Button
              tone="danger"
              disabled={busy}
              onClick={() => {
                if (discarding !== null) void confirm(discarding)
              }}
            >
              Retirer de la tablette
            </Button>
          </>
        }
      />
    </section>
  )
}

function OrphanRow({ row, onDiscard }: { row: OrphanDraft; onDiscard?: () => void }) {
  const { draft, inquiry } = row
  const sync = row.sync === null ? null : SYNC_PRESENTATION[row.sync]
  const address = inquiry === null ? '' : addressLine(inquiry.address)

  return (
    <div className={s.row}>
      <div className={s.body}>
        {/* La référence GHP d'abord : c'est sous ce numéro que l'agent reconnaît son chantier
            et en parle au bureau. La demande reste en cache après son départ de la tournée —
            elle n'y est plus servie, elle n'en est pas effacée. */}
        <span className={s.reference}>{inquiry?.reference ?? 'Contrôle sans référence'}</span>
        {address !== '' && <span className={s.address}>{address}</span>}
        <span className={s.detail}>
          <span>
            Saisie du <time dateTime={draft.updatedAt}>{stampLabel(draft.updatedAt)}</time>
          </span>
          {countLabel(draft.anomalies.length, 'anomalie relevée', 'anomalies relevées')}
          <span className={s.attachments}>
            <Paperclip size={16} aria-hidden />
            {countLabel(row.attachments, 'pièce', 'pièces')}
          </span>
        </span>
        {/* Le « pourquoi » ligne à ligne : la phrase du chapeau dit le cas général, cette date
            dit à l'agent quand sa demande est partie — de quoi la situer dans sa journée. */}
        {draft.outOfTourAt != null && (
          <span className={s.left}>
            Sortie de votre tournée le{' '}
            <time dateTime={draft.outOfTourAt}>{stampLabel(draft.outOfTourAt)}</time>
          </span>
        )}
        {/* Sans référence, l'identifiant du contrôle est le seul repère qui reste pour en
            parler au bureau. */}
        {inquiry?.reference == null && (
          <span className={s.identifier}>Contrôle {draft.inquiryId}</span>
        )}
      </div>

      <div className={s.aside}>
        <Badge tone={sync === null ? 'warn' : sync.tone}>{sync?.label ?? 'Non transmise'}</Badge>
        {onDiscard !== undefined && (
          <Button tone="ghost" onClick={onDiscard}>
            Retirer de la tablette
          </Button>
        )}
        {/* Un vrai lien, comme dans la tournée : ouvrable au doigt comme au clavier, et la
            cible garde la hauteur tactile par la cascade (`--control-h`), pas par un
            composant à part. */}
        <TextLink
          className={s.action}
          render={<Link to="/inspections/$inquiryId" params={{ inquiryId: draft.inquiryId }} />}
        >
          {draft.lockedAt === null ? 'Ouvrir et transmettre' : 'Ouvrir la fiche'}
          <ChevronRight size={16} aria-hidden />
        </TextLink>
      </div>
    </div>
  )
}

/** L'adresse en une ligne, sans les séparateurs des parties que la demande n'a pas (M21). */
function addressLine(address: AssignedInquiry['address']): string {
  const town = [address.postalCode, townOf(address)].filter((part) => part !== null).join(' ')

  return [address.street, town].filter((part) => part !== null && part !== '').join(' · ')
}

/** « 1 pièce » / « 2 pièces » — un compteur au pluriel fautif se lit comme une erreur d'écran. */
function countLabel(count: number, singular: string, plural: string): string {
  return `${count} ${count > 1 ? plural : singular}`
}
